/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { useEffect, useState } from "react";
import { t } from "@superset-ui/core";
import ControlHeader from "src/explore/components/ControlHeader";
import { Select } from "src/components";
import { SelectProps } from "src/components/Select/types";
import { SelectValue, LabeledValue } from "antd/lib/select";

import { ExploreActions } from "src/explore/actions/exploreActions";
import { findAreaMap } from "./mapdata";

type SelectAsyncProps = Omit<SelectProps, "ariaLabel" | "onChange">;

export interface LabelExtendControlProps extends SelectAsyncProps {
    ariaLabel?: string;
    default?: SelectValue;
    multi?: boolean;
    onChange: (val: SelectValue) => void;
    actions: Partial<ExploreActions> & Pick<ExploreActions, "setControlValue">;
    // ControlHeader related props
    description?: string;
    hovered?: boolean;
    label?: string;
    options: { label: string; value: string }[];
    optionsArray: { label: string; value: string }[];
    viz_type: string;
    select_map?: number;
    custom_sort: [];
    areaCode: string;
    areaName: string;
}

function isLabeledValue(arg: any): arg is LabeledValue {
    return arg.value !== undefined;
}

const LabelExtendControl = (props: LabelExtendControlProps): JSX.Element => {
    const {
        ariaLabel,
        onChange,
        placeholder,
        actions,
        options,
        viz_type,
        optionsArray,
        select_map,
        allowClear = true,
        areaCode,
        areaName,
    } = props;

    const [optionLabels, setOptionLabels] = useState<{ label: string; value: string }[]>([]);

    const handleOnChange = (val: SelectValue) => {
        let onChangeVal = val;
        if (Array.isArray(val)) {
            const values = val.map((v) => (isLabeledValue(v) ? v.value : v));
            onChangeVal = values;
            const filtered = optionLabels.filter((item) => val.some((v) => v === item.value));
            actions.setControlValue("label_save", filtered);
        }
        if (isLabeledValue(val)) {
            onChangeVal = val.value;
        }

        onChange(onChangeVal);
    };

    const getValue = () => {
        const currentValue = props.value || (props.default !== undefined ? props.default : undefined);

        // safety check - the value is intended to be undefined but null was used
        if (currentValue === null && !options.find((o) => o.value === null)) {
            return undefined;
        }

        return currentValue;
    };

    useEffect(() => {
        if (viz_type === "area-map-chart") {
            setOptionLabels(optionsArray);
        }
    }, [viz_type, optionsArray]);

    useEffect(() => {
        if (viz_type === "area-select-chart") {
            let optionsArray2: { label: string; value: string }[] = [];
            if (select_map) {
                findAreaMap(select_map).then((geoData: any) => {
                    if (geoData.features) {
                        optionsArray2 = geoData.features.map((dataset: any) => ({
                            label: dataset.properties[areaName],
                            value: dataset.properties[areaCode],
                        }));
                    }
                    setOptionLabels(optionsArray2);
                });
            } else {
                setOptionLabels(optionsArray2);
            }
            // setOptionLabels(optionsArray);
        }
    }, [viz_type, select_map, areaName, areaCode]);

    return (
        <>
            <Select
                allowClear={allowClear}
                ariaLabel={ariaLabel || t("Select ...")}
                value={getValue()}
                header={<ControlHeader {...props} />}
                mode="multiple"
                onChange={handleOnChange}
                options={optionLabels}
                placeholder={placeholder}
            />
        </>
    );
};

export default LabelExtendControl;
